import { FC, useState, useEffect } from "react";
import { VisuallyHidden } from "@react-aria/visually-hidden";
import { SwitchProps, useSwitch } from "@heroui/switch";
import { clsx } from "@heroui/shared-utils";
import { useTranslation } from "react-i18next";

import { useTheme } from "@/hooks/use-theme";
import { SunFilledIcon, MoonFilledIcon } from "@/components/icons";

export interface ThemeSwitchProps {
  className?: string;
  classNames?: SwitchProps["classNames"];
}

export const ThemeSwitch: FC<ThemeSwitchProps> = ({ className, classNames }) => {
  const { t } = useTranslation();

  const [isMounted, setIsMounted] = useState(false);

  const { theme } = useTheme();

  const { Component, slots, isSelected, getBaseProps, getInputProps, getWrapperProps } = useSwitch({
    isSelected: theme === "light",
  });

  useEffect(() => {
    setIsMounted(true);
  }, [isMounted]);

  // Prevent Hydration Mismatch
  if (!isMounted) return <div className="w-6 h-6" />;

  return (
    <Component
      aria-label={isSelected ? t("switch-to-dark-mode") : t("switch-to-light-mode")}
      {...getBaseProps({
        className: clsx(
          "px-px transition-opacity hover:opacity-80 cursor-pointer",
          className,
          classNames?.base,
        ),
      })}
    >
      <VisuallyHidden>
        <input {...getInputProps()} />
      </VisuallyHidden>
      <div
        {...getWrapperProps()}
        className={slots.wrapper({
          class: clsx(
            [
              "w-auto h-auto",
              "bg-transparent",
              "rounded-lg",
              "flex items-center justify-center",
              "group-data-[selected=true]:bg-transparent",
              "!text-default-500 group-data-[selected=true]:text-foreground",
              "pt-px",
              "px-0",
              "mx-0",
            ],
            classNames?.wrapper,
          ),
        })}
      >
        {isSelected ? (
          <div className="flex items-center gap-2">
            <MoonFilledIcon size={22} />
            <span className="text-small font-medium text-default-500">{t("Dark Mode")}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <SunFilledIcon size={22} />
            <span className="text-small font-medium text-default-500">{t("Light Mode")}</span>
          </div>
        )}
      </div>
    </Component>
  );
};
